<template>
  <Teleport to="body">
    <transition name="trial-modal">
      <div v-if="isTrialModal" class="trial-overlay" @click.self="closeModal">
        <div class="trial-panel">
          <!-- 关闭按钮 -->
          <button class="trial-close" @click="closeModal">
            <Icon icon="lucide:x" :size="20" />
          </button>

          <!-- 左侧品牌区 -->
          <div class="trial-brand">
            <div class="brand-badge">限时活动</div>
            <h2 class="brand-title">免费试用 30 天</h2>
            <p class="brand-subtitle">任选3款产品，零成本体验完整功能</p>
            <p class="brand-note">无需绑定信用卡 · 3分钟极速部署</p>

            <div class="brand-steps">
              <div class="brand-step" v-for="(step, i) in steps" :key="i">
                <div class="step-num">{{ i + 1 }}</div>
                <div>
                  <p class="step-title">{{ step.title }}</p>
                  <p class="step-desc">{{ step.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧表单区 -->
          <div class="trial-form">
            <h3 class="form-title">填写试用信息</h3>
            <p class="form-subtitle">提交后客服将在1个工作日内联系您开通</p>

            <div class="form-fields">
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">企业名称 <span class="required">*</span></label>
                  <input
                    v-model="form.company"
                    type="text"
                    placeholder="请输入企业全称"
                    class="form-input"
                    :class="{ 'form-input--error': fieldErrors.company }"
                  />
                  <p v-if="fieldErrors.company" class="field-error">{{ fieldErrors.company }}</p>
                </div>
                <div class="form-group">
                  <label class="form-label">联系人 <span class="required">*</span></label>
                  <input
                    v-model="form.name"
                    type="text"
                    placeholder="请输入姓名"
                    class="form-input"
                    :class="{ 'form-input--error': fieldErrors.name }"
                  />
                  <p v-if="fieldErrors.name" class="field-error">{{ fieldErrors.name }}</p>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">手机号 <span class="required">*</span></label>
                  <input
                    v-model="form.phone"
                    type="tel"
                    placeholder="请输入手机号"
                    class="form-input"
                    :class="{ 'form-input--error': fieldErrors.phone }"
                  />
                  <p v-if="fieldErrors.phone" class="field-error">{{ fieldErrors.phone }}</p>
                </div>
                <div class="form-group">
                  <label class="form-label">邮箱</label>
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="请输入邮箱"
                    class="form-input"
                    :class="{ 'form-input--error': fieldErrors.email }"
                  />
                  <p v-if="fieldErrors.email" class="field-error">{{ fieldErrors.email }}</p>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label"
                  >选择试用产品 <span class="required">*</span>（最多3款）</label
                >
                <div class="product-checks">
                  <label
                    v-for="p in trialProducts"
                    :key="p.id"
                    class="product-check"
                    :class="{ checked: selectedProducts.includes(p.id) }"
                  >
                    <input
                      type="checkbox"
                      :value="p.id"
                      :checked="selectedProducts.includes(p.id)"
                      :disabled="!selectedProducts.includes(p.id) && selectedProducts.length >= 3"
                      class="product-checkbox"
                      @change="toggleProduct(p.id)"
                    />
                    <div class="product-icon" :style="{ background: p.bgColor, color: p.color }">
                      <Icon :icon="p.icon" :size="18" />
                    </div>
                    <div class="product-info">
                      <span class="product-name">{{ p.name }}</span>
                      <span class="product-desc">{{ p.desc }}</span>
                    </div>
                  </label>
                </div>
                <p v-if="fieldErrors.product" class="field-error mt-8px">{{ fieldErrors.product }}</p>
                <p v-else class="select-hint">已选 {{ selectedProducts.length }}/3 款</p>
              </div>

              <div class="form-group">
                <label class="form-label">备注</label>
                <textarea
                  v-model="form.remark"
                  placeholder="请简述您的业务需求..."
                  class="form-textarea"
                  :class="{ 'form-input--error': fieldErrors.remark }"
                ></textarea>
                <p v-if="fieldErrors.remark" class="field-error">{{ fieldErrors.remark }}</p>
              </div>
            </div>

            <p v-if="fieldErrors.general" class="form-error-msg">{{ fieldErrors.general }}</p>
            <button class="submit-btn" :disabled="!canSubmit || submitting" @click="handleSubmit">
              <span v-if="submitting" class="submit-spinner" />
              <Icon v-else icon="lucide:send" :size="16" />
              {{ submitting ? '提交中...' : '提交试用申请' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { Icon } from '@/components/Icon'
import { usePortalState } from './usePortalState'
import { trialProducts } from './data/portalData'
import { ElMessage } from 'element-plus'
import { getSubmitErrorMessage, submitPortalForm } from './usePortalSubmission'

defineOptions({ name: 'TrialModal' })

const { isTrialModal, closeModal } = usePortalState()

const steps = [
  { title: '选择产品', desc: '任选3款试用' },
  { title: '提交申请', desc: '无需付款' },
  { title: '立即开通', desc: '3分钟部署' },
  { title: '体验30天', desc: '决定是否续费' }
]

const form = ref({
  company: '',
  name: '',
  phone: '',
  email: '',
  remark: ''
})

const selectedProducts = ref<string[]>([])
const submitting = ref(false)
const fieldErrors = ref<Record<string, string>>({})

/** 根据错误消息匹配到对应的表单字段 */
function mapErrorToField(msg: string): string {
  if (/姓名|联系人/.test(msg)) return 'name'
  if (/公司|企业/.test(msg)) return 'company'
  if (/手机|电话/.test(msg)) return 'phone'
  if (/邮箱/.test(msg)) return 'email'
  if (/产品/.test(msg)) return 'product'
  if (/备注|内容/.test(msg)) return 'remark'
  return 'general' // 兜底
}

function toggleProduct(id: string) {
  const idx = selectedProducts.value.indexOf(id)
  if (idx >= 0) {
    selectedProducts.value.splice(idx, 1)
  } else if (selectedProducts.value.length < 3) {
    selectedProducts.value.push(id)
  }
}

const canSubmit = computed(() => {
  return (
    form.value.company && form.value.name && form.value.phone && selectedProducts.value.length > 0
  )
})

// 修改输入时清除对应字段错误
watch([() => form.value, selectedProducts], () => { fieldErrors.value = {} }, { deep: true })

async function handleSubmit() {
  if (!canSubmit.value || submitting.value) return
  fieldErrors.value = {}
  submitting.value = true
  try {
    await submitPortalForm({
      username: form.value.name,
      companyName: form.value.company,
      tel: form.value.phone,
      email: form.value.email,
      content: form.value.remark,
      productName: selectedProducts.value.join(','),
      type: 'FREE_TRIAL'
    })
    closeModal()
    form.value = { company: '', name: '', phone: '', email: '', remark: '' }
    selectedProducts.value = []
    ElMessage.success('试用申请已提交！客服将在1个工作日内联系您')
  } catch (error) {
    const msg = getSubmitErrorMessage(error)
    fieldErrors.value[mapErrorToField(msg)] = msg
  } finally {
    submitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.trial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.trial-panel {
  background: #fff;
  border-radius: 20px;
  max-width: 880px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.trial-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  z-index: 10;
  transition:
    background 0.2s,
    color 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
}

// 左侧品牌区
.trial-brand {
  flex-shrink: 0;
  width: 340px;
  background: linear-gradient(160deg, #0066ff 0%, #00b96b 100%);
  padding: 40px 32px;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.brand-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 4px 16px;
  font-size: 13px;
  margin-bottom: 20px;
  align-self: flex-start;
}

.brand-title {
  font-size: 32px;
  font-weight: 900;
  margin-bottom: 12px;
  line-height: 1.2;
}

.brand-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.brand-note {
  font-size: 13px;
  opacity: 0.7;
  margin-bottom: 36px;
}

.brand-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.brand-step {
  display: flex;
  align-items: center;
  gap: 14px;

  .step-num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .step-title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 2px;
  }

  .step-desc {
    font-size: 12px;
    opacity: 0.75;
    margin: 0;
  }
}

// 右侧表单区
.trial-form {
  flex: 1;
  padding: 40px 36px;
  overflow-y: auto;
}

.form-title {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 6px;
}

.form-subtitle {
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 28px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 6px;
  font-weight: 500;
}

.required {
  color: #e11d48;
}

.form-input {
  height: 44px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #0066ff;
  }

  &::placeholder {
    color: #d1d5db;
  }
}

.form-textarea {
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    border-color: #0066ff;
  }

  &::placeholder {
    color: #d1d5db;
  }
}

.product-checks {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.product-check {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0066ff;
    background: #f9fafb;
  }

  &.checked {
    border-color: #0066ff;
    background: #ebf5ff;
  }

  &.checked .product-checkbox {
    background: #0066ff;
    border-color: #0066ff;
  }
}

.product-checkbox {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: #0066ff;
}

.product-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.product-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.product-desc {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
}

.submit-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #0066ff, #0044cc);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 28px;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0, 102, 255, 0.35);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.submit-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-input--error {
  border-color: #DC2626 !important;
}

.field-error {
  margin: 4px 0 0;
  font-size: 12px;
  color: #DC2626;
  line-height: 1.4;
}

.mt-8px { margin-top: 8px; }

.form-error-msg {
  margin: 12px 0 -12px;
  padding: 10px 14px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  color: #DC2626;
  font-size: 13px;
  line-height: 1.5;
}

// 过渡动画
.trial-modal-enter-active {
  transition: opacity 0.25s ease;

  .trial-panel {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }
}

.trial-modal-leave-active {
  transition: opacity 0.2s ease;

  .trial-panel {
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
  }
}

.trial-modal-enter-from {
  opacity: 0;

  .trial-panel {
    transform: scale(0.95);
    opacity: 0;
  }
}

.trial-modal-leave-to {
  opacity: 0;

  .trial-panel {
    transform: scale(0.97);
    opacity: 0;
  }
}

@media (max-width: 768px) {
  .trial-panel {
    flex-direction: column;
  }

  .trial-brand {
    width: 100%;
    padding: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .product-checks {
    grid-template-columns: 1fr;
  }
}
</style>
